const TINY_PACKAGE_ASSETS_URL =
  "https://assets.getredo.com/marketing/games/tiny-package";

// Physics constants
export const PHYSICS = {
  GRAVITY: 9.8, // m/s²
  PTM_RATIO: 32, // pixels per meter
} as const;

// Player constants
export const PLAYER = {
  RADIUS: 0.5, // meters (16 pixels)
  LINEAR_DAMPING: 0.05, // 5% velocity damping per frame
  MIN_VELOCITY_X: 2.0, // m/s minimum forward speed
  MAX_VELOCITY_Y: 20.0, // m/s terminal velocity
  DIVE_FORCE: 100.0, // downward force when diving
  WAKE_IMPULSE_X: 1.0, // m/s
  WAKE_IMPULSE_Y: 2.0, // m/s
  GROUND_DAMPING_FACTOR: 0.5, // Less damping on ground
  DIVE_FORCE_GROUND_FACTOR: 0.1, // Reduced dive force on ground
  DIVE_FORCE_AIR_FACTOR: 0.3, // Reduced dive force in air
} as const;

// Terrain generation constants
export const TERRAIN = {
  HILL_WIDTH: 200, // pixels per hill
  MIN_HILL_HEIGHT: 60, // pixels
  MAX_HILL_HEIGHT: 120, // pixels
  NUM_HILLS: 30,
  SEGMENTS_PER_HILL: 10, // For smooth curves
  PADDING_TOP: 40,
  PADDING_BOTTOM: 40,
  CLEANUP_DISTANCE: 5000, // Keep terrain this far behind camera
  CLEANUP_THRESHOLD: 50, // Minimum points before cleanup
  CLEANUP_INTERVAL: 60, // Frames between cleanups
} as const;

// Background terrain constants
export const BACKGROUND_TERRAIN = {
  FAR_LAYER: {
    PARALLAX_SPEED: 0.1, // 10% of camera speed
    HEIGHT_OFFSET: -180, // Far mountains - much higher in sky
    COLOR: [80, 100, 120], // Blue-gray mountains
    OPACITY: 0.4,
  },
  NEAR_LAYER: {
    PARALLAX_SPEED: 0.4, // 40% of camera speed
    HEIGHT_OFFSET: -80, // Closer hills - moderately higher
    COLOR: [100, 140, 100], // Green hills
    OPACITY: 0.6,
  },
} as const;

// Camera constants
export const CAMERA = {
  BASELINE_FACTOR: 0.7, // Where the baseline sits on screen
  MIN_SCALE: 0.7,
  VIEWPORT_HEIGHT_FACTOR: 0.8,
  MIN_PLAYER_BASELINE_DISTANCE: 300,
  SCALE_SMOOTHING: 0.05,
  POSITION_SMOOTHING: 0.1,
  PLAYER_SCREEN_POSITION: 0.15, // 15% from left edge
  SHAKE_SPEED_MULTIPLIER: 0.3, // Multiplier for speed-based shake intensity
  SHAKE_DURATION_MULTIPLIER: 0.02, // Multiplier for speed-based shake duration
} as const;

// Game constants
export const GAME = {
  START_X: 60,
  TARGET_DISTANCE: 80_000,
  LANDING_ANGLE_THRESHOLD: Math.PI / 4, // 45 degrees
  GOOD_LANDING_SPEED_RETENTION: 1.0, // Keep 100% speed
  BAD_LANDING_SPEED_RETENTION: 0.3, // Keep 30% speed
  TRAIL_LENGTH: 10,
} as const;

// NPC opponent constants
export const NPC = {
  GROUND_SPEED: {
    easy: 16, // m/s - slower for easy difficulty
    medium: 18, // m/s - normal speed
    hard: 20, // m/s - faster for hard difficulty
  },
  RADIUS: 0.5, // meters (same size as player)
  TRAIL_LENGTH: 10,
  START_OFFSET: 200, // Start behind player
} as const;

// Speed line particle constants
export const SPEED_LINES = {
  MAX_PARTICLES: 20,
  SPAWN_RATE: 0.05, // Seconds between spawns
  PARTICLE_LIFETIME: 1.2, // Seconds - longer lifetime for trail effect
  MAX_LINE_LENGTH: 40, // Maximum pixels for trail length
  MIN_LINE_LENGTH: 5, // Minimum pixels for trail length
  LINE_WIDTH: 2, // Pixels
  SPEED_MULTIPLIER: 200, // Pixels per second
  SPAWN_RADIUS: 25, // Pixels around player - much tighter
  DOWNWARD_SPEED_THRESHOLD: 3.0, // m/s minimum downward speed to spawn particles
  VELOCITY_FACTOR: 0.4, // Particles move at 40% of player velocity
  DECELERATION_FACTOR: 0.92, // Particles decelerate over time (multiply by this each frame)
} as const;

// Day/Night cycle constants
export const DAY_NIGHT_CYCLE = {
  CYCLE_DURATION: 120, // seconds for full day/night cycle
} as const;

// Cloud system constants
export const CLOUDS = {
  INITIAL_COUNT: 4, // Reduced initial clouds to prevent overcrowding
  MIN_SIZE: 30, // Minimum cloud size
  MAX_SIZE: 200, // Maximum cloud size
  MIN_HEIGHT: -120, // Much higher in sky (expanded range)
  MAX_HEIGHT: 120, // Lower clouds too (expanded range)
  MIN_SPEED: 0.5, // Slower minimum speed for variety
  MAX_SPEED: 12, // Maximum cloud speed (pixels per second)
  SPAWN_INTERVAL_MIN: 0.8, // Slower spawn rate to reduce cloud density
  SPAWN_INTERVAL_MAX: 4.0, // Longer intervals between clusters
  CLEANUP_DISTANCE: 2000, // Distance behind camera to keep clouds
  GENERATION_DISTANCE: 5000, // Distance ahead of camera to generate clouds
  // Cluster spawning settings
  CLUSTER_SIZE_MIN: 1, // Allow single clouds sometimes
  CLUSTER_SIZE_MAX: 4, // Smaller maximum cluster size
  CLUSTER_SPREAD_X: 600, // Wider horizontal spread for better distribution
  CLUSTER_SPREAD_Y: 150, // Much larger vertical spread for varied heights
} as const;

// Rendering constants
export const RENDERING = {
  SKY_COLOR: [135, 206, 235],
  TERRAIN_COLOR: [34, 139, 34],
  TERRAIN_OUTLINE_COLOR: [0, 100, 0],
  TERRAIN_OUTLINE_WIDTH: 3,
  TEXT_OUTLINE_COLOR: [0, 0, 0],
  TEXT_COLOR: [255, 255, 255],
} as const;

// Sky gradient colors for different times of day
export const SKY_GRADIENTS = {
  DAWN: {
    TOP: [255, 94, 77], // Warm red-orange
    MIDDLE: [255, 154, 0], // Orange
    BOTTOM: [255, 206, 84], // Yellow
  },
  DAY: {
    TOP: [87, 167, 255], // Bright blue
    MIDDLE: [135, 206, 235], // Sky blue
    BOTTOM: [173, 216, 230], // Light blue
  },
  DUSK: {
    TOP: [255, 94, 77], // Deep red-orange
    MIDDLE: [255, 140, 105], // Coral
    BOTTOM: [255, 206, 84], // Golden yellow
  },
  NIGHT: {
    TOP: [25, 25, 112], // Midnight blue
    MIDDLE: [72, 61, 139], // Dark slate blue
    BOTTOM: [123, 104, 238], // Medium slate blue
  },
} as const;

export { TINY_PACKAGE_ASSETS_URL };
